import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from 'components/ui/button';
import { CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { AuthLayout, AuthCard, AuthPhoneField, AuthPasswordField, OtpVerify } from 'components/auth';
import { useStartPhoneRecover, useResendPhoneRecover, useCompletePhoneRecover } from 'modules/auth';
import { useToast } from 'components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import { UZ_E164_PATTERN } from 'lib/phone';

const makePhoneSchema = (t: TFunction) =>
  z.object({
    phone: z.string().regex(UZ_E164_PATTERN, t('auth.invalid_phone')),
  });
type PhoneFormData = z.infer<ReturnType<typeof makePhoneSchema>>;

// The 6-digit code is driven by plain state (see below), so the reset form only
// owns the two password fields and their cross-field match check.
const makeResetSchema = (t: TFunction) =>
  z
    .object({
      newPassword: z.string().min(8, t('auth.password_min')),
      confirmPassword: z.string().min(8, t('auth.password_min')),
    })
    .refine((d) => d.newPassword === d.confirmPassword, {
      path: ['confirmPassword'],
      message: t('auth.passwords_no_match'),
    });
type ResetFormData = z.infer<ReturnType<typeof makeResetSchema>>;

// Seconds the resend button stays disabled after a code is sent. Mirrors the
// server's resend cooldown and OtpVerify's default.
const RESEND_COOLDOWN_SECONDS = 60;

// Reset flow is stashed in sessionStorage so a page refresh on the code step
// resumes there (with the remaining cooldown) instead of dropping the user back
// to the phone step. sessionStorage (not localStorage) keeps it tab-scoped and
// short-lived, which suits a recovery challenge.
const RECOVER_STORAGE_KEY = 'examing.pwRecover';

interface RecoverSession {
  phone: string;
  phoneHint: string;
  challengeRef: string;
  /** ISO expiry of the OTP itself — a stale challenge is not worth resuming. */
  otpExpiresAt: string;
  /** Epoch ms the resend cooldown counts down to. */
  resendUntil: number;
}

const readRecoverSession = (): RecoverSession | null => {
  try {
    const raw = sessionStorage.getItem(RECOVER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RecoverSession>;
    if (!parsed.challengeRef || !parsed.otpExpiresAt) return null;
    // The code itself has expired — nothing left to resume.
    if (Date.parse(parsed.otpExpiresAt) <= Date.now()) {
      sessionStorage.removeItem(RECOVER_STORAGE_KEY);
      return null;
    }
    return parsed as RecoverSession;
  } catch {
    return null;
  }
};

const writeRecoverSession = (session: RecoverSession) => {
  try {
    sessionStorage.setItem(RECOVER_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Storage unavailable (private mode / quota) — the flow still works in
    // memory for the current page, it just won't survive a refresh.
  }
};

const clearRecoverSession = () => {
  try {
    sessionStorage.removeItem(RECOVER_STORAGE_KEY);
  } catch {
    // ignore
  }
};

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const navState = (location.state as { phone?: string }) || {};

  const startRecover = useStartPhoneRecover();
  const resendRecover = useResendPhoneRecover();
  const completeRecover = useCompletePhoneRecover();

  // Restore an in-flight reset once, on mount, so a refresh lands back on the
  // code step rather than the phone step.
  const [restored] = React.useState<RecoverSession | null>(() => readRecoverSession());

  const [step, setStep] = React.useState<'phone' | 'reset'>(restored ? 'reset' : 'phone');
  const [phone, setPhone] = React.useState(restored?.phone || '');
  // Masked destination from the server, shown on the reset step.
  const [phoneHint, setPhoneHint] = React.useState(restored?.phoneHint || '');
  const [challengeRef, setChallengeRef] = React.useState(restored?.challengeRef || '');
  // Absolute deadline the OtpVerify resend cooldown counts down to.
  const [cooldownUntil, setCooldownUntil] = React.useState(restored?.resendUntil || 0);
  // The 6-digit code lives here (not in the reset form) — driving the OTP boxes
  // through a react-hook-form Controller swallowed typed/pasted input, so this
  // mirrors the proven plain-state wiring used by the signup verify step.
  const [code, setCode] = React.useState('');
  const [codeError, setCodeError] = React.useState<string | null>(null);

  const phoneForm = useForm<PhoneFormData>({
    resolver: zodResolver(makePhoneSchema(t)),
    defaultValues: { phone: restored?.phone || navState.phone || '' },
  });
  const resetForm = useForm<ResetFormData>({ resolver: zodResolver(makeResetSchema(t)) });

  const toastError = (error: unknown, fallback: string) => {
    const err = error as { response?: { data?: { message?: string } } };
    toast.error(t('error'), { description: err?.response?.data?.message || fallback });
  };

  const onSubmitPhone = async (data: PhoneFormData) => {
    try {
      const res = await startRecover.mutateAsync({ phone: data.phone });
      const resendUntil = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
      setPhone(data.phone);
      setPhoneHint(res.phoneHint);
      setChallengeRef(res.challengeRef);
      setCode('');
      setCodeError(null);
      setCooldownUntil(resendUntil);
      setStep('reset');
      writeRecoverSession({
        phone: data.phone,
        phoneHint: res.phoneHint,
        challengeRef: res.challengeRef,
        otpExpiresAt: res.expiresAt,
        resendUntil,
      });
    } catch (error) {
      toastError(error, t('auth.could_not_send_code'));
    }
  };

  const onResend = async () => {
    try {
      const res = await resendRecover.mutateAsync({ challengeRef });
      const resendUntil = Date.now() + RESEND_COOLDOWN_SECONDS * 1000;
      setCode('');
      setCodeError(null);
      setCooldownUntil(resendUntil);
      writeRecoverSession({
        phone,
        phoneHint,
        challengeRef,
        otpExpiresAt: res.expiresAt,
        resendUntil,
      });
      toast.success(t('success'), { description: t('auth.code_resent') });
    } catch (error) {
      toastError(error, t('auth.could_not_send_code'));
    }
  };

  const onChangePhone = () => {
    clearRecoverSession();
    setStep('phone');
    setCode('');
    setCodeError(null);
  };

  const onSubmitReset = async (data: ResetFormData) => {
    if (!/^\d{6}$/.test(code)) {
      setCodeError(t('auth.code_6_digits'));
      return;
    }
    try {
      await completeRecover.mutateAsync({ challengeRef, code, newPassword: data.newPassword });
      clearRecoverSession();
      toast.success(t('success'), { description: t('auth.password_reset_success') });
      navigate('/login', { replace: true });
    } catch (error) {
      toastError(error, t('auth.could_not_reset'));
    }
  };

  return (
    <AuthLayout>
      <AuthCard>
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-2xl font-bold">{t('forgot_password')}</CardTitle>
          <CardDescription>
            {step === 'phone' ? t('auth.recover_intro') : t('auth.verify_phone_desc', { phone: phoneHint || phone })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'phone' ? (
            <form onSubmit={phoneForm.handleSubmit(onSubmitPhone)} className="space-y-5">
              <Controller
                name="phone"
                control={phoneForm.control}
                render={({ field, fieldState }) => (
                  <AuthPhoneField
                    autoFocus
                    label={t('auth.phone_number')}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Button type="submit" className="group w-full" size="lg" disabled={startRecover.isPending}>
                {startRecover.isPending ? t('auth.sending_code') : t('auth.send_code')}
                {!startRecover.isPending && (
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={resetForm.handleSubmit(onSubmitReset)} className="space-y-5">
              <OtpVerify
                value={code}
                onChange={(next) => {
                  if (codeError) setCodeError(null);
                  setCode(next);
                }}
                error={codeError}
                disabled={completeRecover.isPending}
                onResend={onResend}
                resendPending={resendRecover.isPending}
                resendNonce={0}
                cooldownUntil={cooldownUntil}
                cooldownSeconds={RESEND_COOLDOWN_SECONDS}
                autoFocus
              />
              <AuthPasswordField
                autoComplete="new-password"
                label={t('auth.new_password')}
                placeholder={t('at_least_8_characters')}
                error={resetForm.formState.errors.newPassword?.message}
                {...resetForm.register('newPassword')}
              />
              <AuthPasswordField
                autoComplete="new-password"
                label={t('auth.confirm_password')}
                placeholder={t('auth.confirm_password')}
                error={resetForm.formState.errors.confirmPassword?.message}
                {...resetForm.register('confirmPassword')}
              />
              <Button type="submit" className="group w-full" size="lg" disabled={completeRecover.isPending}>
                {completeRecover.isPending ? t('auth.resetting') : t('auth.reset_password')}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={onChangePhone}
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t('auth.change_phone')}
                </button>
              </div>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-muted-foreground">
            <Link to="/login" className="font-semibold text-foreground underline underline-offset-[3px] hover:decoration-2">
              {t('back_to_sign_in')}
            </Link>
          </p>
        </CardContent>
      </AuthCard>
    </AuthLayout>
  );
};

export default ForgotPassword;
